/**
 * Avatar helpers (2026-07-26)
 * ===========================
 *
 * Small, dependency-free utility layer that keeps every render of a
 * user avatar consistent across the header, the account switcher,
 * chat, and any future surface.
 *
 * Rendering rule (WhatsApp-style):
 *   1. If `avatar_url` (from `/auth/me`) is present, render it inside
 *      an <img> with `object-fit: cover`.
 *   2. Otherwise render the first 1-2 letters of the user's name
 *      inside a coloured circle. Colour is deterministic from the
 *      user's id so the same person always gets the same swatch.
 *
 * NO external CDN — avatars are served by `/api/users/{uid}/avatar`.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Convert an /auth/me `avatar_url` (relative path) into an absolute
 * URL the <img> tag can consume. Leaves external URLs untouched so
 * a future "profile from Google" flow keeps working.
 */
export function resolveAvatarUrl(rawUrl) {
  if (!rawUrl) return null;
  if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
  const base = API_URL.replace(/\/$/, '');
  return `${base}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
}

/**
 * Absolute avatar URL by user id. Adds a cache-busting `?v=…` when
 * the caller passes a version — matches the backend's per-upload
 * version bump so the browser refetches on new photo.
 */
export function avatarUrlForUser(userId, version) {
  if (!userId) return null;
  const base = API_URL.replace(/\/$/, '');
  const query = version != null ? `?v=${encodeURIComponent(version)}` : '';
  return `${base}/api/users/${encodeURIComponent(userId)}/avatar${query}`;
}

/**
 * Negative avatar cache (Aug 2026).
 * Production logged 45k+ avatar 404s/day: every list/chat/live-room
 * render blindly fetched `/api/users/{id}/avatar` for users WITHOUT a
 * photo. Edge proxies strip Cache-Control on API 404s, so the browser
 * refetched on every mount. This module-level cache remembers misses
 * for 10 minutes across ALL components in the SPA session.
 */
const _avatarMisses = new Map(); // userId -> expiry epoch-ms
const _MISS_TTL_MS = 10 * 60 * 1000;

export function isKnownAvatarMiss(userId) {
  if (!userId) return false;
  const exp = _avatarMisses.get(String(userId));
  if (!exp) return false;
  if (Date.now() > exp) { _avatarMisses.delete(String(userId)); return false; }
  return true;
}

export function markAvatarMiss(userId) {
  if (userId) _avatarMisses.set(String(userId), Date.now() + _MISS_TTL_MS);
}

export function clearAvatarMiss(userId) {
  if (userId) _avatarMisses.delete(String(userId));
}

/** Absolute cover-background URL by user id (Aug 2026). */
export function coverUrlForUser(userId, version) {
  if (!userId) return null;
  const base = API_URL.replace(/\/$/, '');
  const query = version != null ? `?v=${encodeURIComponent(version)}` : '';
  return `${base}/api/users/${encodeURIComponent(userId)}/cover${query}`;
}

/**
 * "أحمد محمد الفهد" → "أح", "Ali Salem" → "AS".
 * Falls back to a rocket emoji for genuinely unnamed accounts so the
 * circle is never blank.
 */
export function getInitials(name) {
  const raw = (name || '').trim();
  if (!raw) return '👤';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
}

// Curated palette — professional, distinguishable, WCAG-AA on white.
const _PALETTE = [
  '#0E7490', '#0369A1', '#7C3AED', '#B45309', '#B91C1C',
  '#047857', '#4338CA', '#BE185D', '#0F766E', '#78350F',
  '#334155', '#5B21B6',
];

/**
 * Deterministic colour for an id/string — same input, same swatch.
 */
export function avatarBackgroundFor(seed) {
  const s = String(seed || '');
  if (!s) return _PALETTE[0];
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return _PALETTE[Math.abs(h) % _PALETTE.length];
}
